import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { getUserId } from "../lib/user";
import { googleLogin, logout } from "../lib/auth";
import { STAGES, statusToIndex, C } from "../lib/theme";
import { ArrowRight, Diamond, Loader2, LogIn, LogOut, Settings, Trophy } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);
import SettingsDialog from "../components/SettingsDialog";
import { toast } from "sonner";

const CHIPS = [
  "Why is the sky blue?",
  "How does compound interest work?",
  "What is a neural network?",
  "How do quadratic equations work?",
  "What actually causes inflation?",
  "How does a bill become a law?",
];

const WHY = [
  { t: "Pre-testing", d: "Being tested before you learn primes memory — even when you guess wrong.", c: C.vio },
  { t: "Retrieval practice", d: "Pulling information out beats reading it in. Every stage makes you recall.", c: C.blu },
  { t: "The Feynman technique", d: "If you can't explain it simply, you don't understand it yet.", c: C.amb },
  { t: "Calibration", d: "Rating your confidence exposes what you think you know but don't.", c: C.grn },
  { t: "Generative learning", d: "You predict, connect and complete while you learn — generating beats reading.", c: C.lim },
];

export default function Home() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);
  const [recent, setRecent] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    getUserId();
    const p = new URLSearchParams(window.location.search).get("topic");
    if (p) setTopic(p);
    api.getUser(getUserId()).then(setMe).catch(() => {});
    api.listSessions(getUserId()).then(setRecent).catch(() => {});
  }, []);

  const begin = async (t) => {
    const value = (t || topic).trim();
    if (!value || loading) return;
    setLoading(true);
    try {
      const s = await api.createSession(value);
      navigate(`/s/${s.id}`);
    } catch (e) {
      toast.error("Couldn't start that topic. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto" data-testid="home-page">
      {/* top bar */}
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md" style={{ background: `${C.blu}1f`, border: `1px solid ${C.blu}55` }}>
            <span className="font-display text-base" style={{ color: C.blu }}>F</span>
          </div>
          <span className="font-display text-lg">Fathom</span>
        </div>
        {me?.authed ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setSettingsOpen(true)} data-testid="home-settings" className="press rounded-full border border-hair p-2 text-muted hover:text-ink" title="Settings"><Settings className="h-4 w-4" /></button>
            {me.picture && <img src={me.picture} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />}
            <span className="hidden text-sm text-ink sm:inline">{me.displayName}</span>
            <button onClick={logout} data-testid="home-sign-out" className="press inline-flex items-center gap-1.5 rounded-full border border-hair px-3 py-1.5 text-xs text-muted hover:text-ink"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setSettingsOpen(true)} data-testid="home-settings" className="press rounded-full border border-hair p-2 text-muted hover:text-ink" title="Settings"><Settings className="h-4 w-4" /></button>
            <button onClick={googleLogin} data-testid="home-sign-in" className="press inline-flex items-center gap-1.5 rounded-full border border-hair px-4 py-1.5 text-xs text-ink hover:bg-white/5">
              <LogIn className="h-3.5 w-3.5" /> Sign in with Google
            </button>
          </div>
        )}
      </div>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* HERO */}
      <section className="mx-auto flex max-w-3xl flex-col items-center px-6 pt-16 text-center sm:pt-24">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-hair bg-surface px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted anim-fade-up">
          <Diamond className="h-3 w-3" style={{ color: C.blu }} /> Check · Learn · Teach · Prove
        </div>
        <h1 className="font-display text-4xl font-medium leading-[1.05] sm:text-5xl lg:text-6xl anim-fade-up" style={{ animationDelay: "60ms" }}>
          Learn anything so well<br />you could <em className="italic" style={{ color: C.blu }}>teach</em> it.
        </h1>
        <p className="mt-6 max-w-xl text-base text-muted anim-fade-up" style={{ animationDelay: "120ms" }}>
          Type a topic. Fathom checks what you already know, teaches the gaps, makes you explain it to a five-year-old, then proves you've got it. About 30 minutes.
        </p>

        <div className="mt-9 w-full anim-fade-up" style={{ animationDelay: "180ms" }}>
          <div className="flex items-center gap-2 rounded-2xl border border-hair bg-surface p-2 focus-within:border-blu/60">
            <input
              data-testid="topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && begin()}
              placeholder="e.g. How do vaccines train the immune system?"
              className="w-full bg-transparent px-3 py-3 text-base text-ink placeholder:text-muted focus:outline-none"
            />
            <button
              data-testid="begin-btn"
              onClick={() => begin()}
              disabled={loading}
              className="flex shrink-0 items-center gap-2 rounded-xl bg-blu px-5 py-3 text-sm font-semibold text-bg transition-transform hover:scale-[1.03] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Begin <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {CHIPS.map((c) => (
              <button
                key={c}
                data-testid="suggestion-chip"
                onClick={() => begin(c)}
                disabled={loading}
                className="rounded-full border border-hair bg-surface px-3 py-1.5 text-xs text-muted transition-colors hover:border-blu/50 hover:text-ink"
              >
                {c}
              </button>
            ))}
          </div>
          <p className="mt-5 text-xs text-muted">
            Built on the pre-testing effect, generative learning, retrieval practice, the Feynman technique and confidence calibration.
          </p>
        </div>
      </section>

      {/* CONTINUE — a returning learner's first job is picking up the thread,
          not typing a new topic */}
      {recent.length > 0 && (
        <section className="mx-auto mt-16 max-w-3xl px-6" data-testid="continue-section">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-display text-lg text-ink">Jump back in</h2>
            <span className="mono-label">{recent.length} topic{recent.length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {recent.slice(0, 6).map((r) => {
              const mastered = r.status === "mastered";
              const stage = STAGES[Math.min(statusToIndex(r.status), 3)];
              const color = mastered ? C.grn : stage.color;
              return (
                <button
                  key={r.id}
                  data-testid={`continue-${r.id}`}
                  onClick={() => navigate(`/s/${r.id}`)}
                  className="lift group rounded-lg border border-hair bg-surface p-4 text-left transition-colors hover:bg-surface2"
                  style={{ borderLeft: `3px solid ${color}` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider" style={{ color }}>
                      {mastered ? <><Trophy className="h-3 w-3" /> Mastered</> : <>{stage.n} · {stage.name}</>}
                    </span>
                    <span className="font-mono text-[10px] text-muted">{dayjs(r.updatedAt).fromNow()}</span>
                  </div>
                  <div className="mt-1.5 line-clamp-2 text-sm font-medium text-ink">{r.topic}</div>
                  <div className="mt-2 flex items-center gap-1 text-xs text-muted opacity-0 transition-opacity group-hover:opacity-100">
                    {mastered ? "Review your study card" : "Continue"} <ArrowRight className="h-3 w-3" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="mx-auto mt-28 max-w-5xl px-6">
        <h2 className="mb-6 font-display text-lg text-ink">How it works</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {STAGES.map((s) => (
            <div key={s.key} className="rounded-xl border border-hair bg-surface p-5" style={{ borderTop: `2px solid ${s.color}` }}>
              <div className="font-mono text-xs" style={{ color: s.color }}>{s.n} · {s.name}</div>
              <div className="mt-2 font-display text-lg text-ink">
                {s.key === "check" && "See what you already know"}
                {s.key === "learn" && "A lesson built on your gaps"}
                {s.key === "teach" && "Explain it to a five-year-old"}
                {s.key === "prove" && "A hard test, with confidence"}
              </div>
              <div className="mt-1 text-sm text-muted">
                {s.key === "check" && "Four quick questions to set your baseline and prime memory."}
                {s.key === "learn" && "Deep but simple, with interactive checks and real resources."}
                {s.key === "teach" && "Pip exposes every gap in your explanation, one by one."}
                {s.key === "prove" && "Application questions you must reason through, not recall."}
              </div>
              <div className="mt-3 font-mono text-[11px] text-muted">{s.time}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted">
          Don't pass Prove? You go back to Learn with a review built around exactly what you missed — not back to the start.
        </p>
      </section>

      {/* WHY IT WORKS */}
      <section className="mx-auto mt-24 max-w-5xl px-6">
        <h2 className="mb-6 font-display text-lg text-ink">Why it works</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w) => (
            <div key={w.t} className="rounded-xl border border-hair bg-surface p-5">
              <div className="mb-2 h-1 w-8 rounded-full" style={{ background: w.c }} />
              <div className="font-display text-base text-ink">{w.t}</div>
              <div className="mt-1 text-sm text-muted">{w.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="mx-auto mt-24 max-w-5xl border-t border-hair px-6 py-10">
        <div className="font-display text-xl">Fathom</div>
        <div className="mt-1 text-sm text-muted">Learn it until you can teach it.</div>
        <Link to="/privacy" className="press mono-label mt-4 inline-block text-muted hover:text-ink" data-testid="home-privacy-link">
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
